import {
  Component,
  Expressions,
  Plugins,
  stringifyExpression,
  type Stoppable,
} from '@packages/systems/wfdl';
import {
  EList,
  EHole,
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import type {JSONNode} from '@packages/shared/types/JSONNode';

import {
  parseDefaultData,
  reifyToExpressions,
} from '@packages/systems/core/render';
import {exportElementAsJSONNodeArray} from '@packages/systems/tree';

import * as JsonDomUtils from '@packages/systems/core/utils/JsonDomUtils';
import * as DomTypeUtils from '@packages/systems/core/utils/DomTypeUtils';
import {weakMemo} from '@packages/utilities/memo';
import {qualify} from '@packages/systems/wfdl-Qualified';
import type {ReportType, NodePatch, Context} from '../types';
import {type EnhancedExtraContext} from '../ExtraContext';

/**
 * `defaultData` lives on a component root as a serialized WFDL Function
 * expression whose body is a Record. Any expression under that Record body may
 * hold Element expressions — a single `Element`, a `List<Element>` (typical
 * for slot defaults), or an Element nested inside some other shape (e.g. a
 * `Case` arm or a `Function` argument).
 *
 * This util mirrors `DefaultValueMigrationUtils` but walks the *expression*
 * shape at `jsonNode.data.sym.defaultData` instead of the POJO shape at
 * `system.defaultValue` on `dataType`. For every Element expression we find
 * while walking the tree, we:
 *
 *   1. Read the Element expression at rest.
 *   2. Convert it into JSONNode subtrees (via `exportElementAsJSONNodeArray`).
 *   3. Run the caller-supplied patch over the subtree.
 *   4. Reify the resulting JSONNodes back into Expression(s) and swap them
 *      in place.
 *
 * The write-back rule depends on where the Element sits:
 *
 * - Bare `Element` positions (Record fields, Case arms, Function bodies,
 *   etc.) hold exactly one expression. We require the patch to produce
 *   exactly one reified root. 0 or >1 roots have no safe in-place rewrite
 *   here — warn and leave the element untouched.
 * - `List<Element>` positions can hold any number of expressions. Each item
 *   in the list is patched independently, and the reified roots from every
 *   item are spliced back into a single new list in order. This means a
 *   patch that removes a node (0 roots) shrinks the list, and one that
 *   splits a node (>1 roots) grows the list — mirroring the shape-preserving
 *   contract in `_updateDefaultValueSubtree` on the `defaultValue` path.
 *
 * We do not recurse *inside* an Element that we already handled — patches
 * receive the whole subtree at once, matching the `defaultValue` contract.
 */

type PatchCallback = (jsonNodeTree: Array<JSONNode>) => {
  nodes: Array<JSONNode>;
  changed: boolean;
};

type WarningReporter = (msg: string, warning: string) => void;

type PatchElementResult =
  | {changed: false}
  | {changed: true; expressions: Array<Expression>};

/**
 * Walk every Element reachable in the defaultData expression and apply the
 * callback. Returns the walked expression — the same reference as the input
 * when nothing changed, so consumers can compare by reference to detect a
 * change.
 *
 * Traversal uses `Expressions.everywhereStoppable`. Two node types are
 * handled specially:
 *
 *  - `Element`: patch the whole subtree in one shot, require exactly one
 *    reified root, and `stop` so we don't recurse into the element's
 *    children (patches see the whole subtree at once, matching the
 *    `defaultValue` contract). 0 or >1 roots trigger a
 *    `MalformedDefaultDataPatch` warning and the element is left untouched.
 *  - `List` containing at least one Element item: patch each Element item
 *    independently, splice all reified roots (0, 1, or many per item) into
 *    a new list in order, and `stop`. Non-Element items in the same list
 *    (e.g. holes) get recursively walked so nested Elements inside them
 *    still get visited. A patch that returns `[]` for an item shrinks the
 *    list; one that returns multiple nodes grows it. This matches
 *    `_updateDefaultValueSubtree` on the `defaultValue` path.
 *
 * All other expression shapes `resume` so `everywhereStoppable` continues
 * walking into their children.
 */
const walkDefaultDataElements = (
  defaultData: Expression,
  plugins: Plugins.PluginsType,
  dataTypes: Plugins.DataTypes,
  cb: PatchCallback,
  reportWarning: WarningReporter,
  jsonNodeId: string
): Expression => {
  const components = componentsFromPluginsAndDataTypes(plugins)(dataTypes);

  const patchElement = (element: ExpressionElement): PatchElementResult => {
    // Safe to use the memoized export even though patches mutate the nodes
    // they receive: the defaultData expression is parsed fresh on every
    // pass, so the memo is keyed on short-lived element objects. Each
    // Element is exported at most once per pass, and one whose nodes get
    // mutated is either replaced in the tree by the reified result or
    // dropped with the unpersisted parse (rejected patches), so a mutated
    // cache entry is never read again. Patches that change nothing leave
    // the nodes untouched, so their cache entries stay valid.
    const asJsonNodes = exportElementAsJSONNodeArray(components)(element);
    const {nodes: patched, changed} = cb(asJsonNodes);

    if (!changed) return {changed: false};

    return {
      changed: true,
      expressions: reifyToExpressions(patched, dataTypes, plugins),
    };
  };

  const walker = (expr: Expression): Stoppable<Expression> => {
    // List<Element> (or a mixed list with at least one Element): rewrite
    // the whole list here so patches can add, remove, or split items.
    const items = Expressions.getList(expr);
    if (items && items.some(Expressions.isElement)) {
      let listChanged = false;
      const nextItems: Array<Expression> = [];

      for (const item of items) {
        const element = Expressions.getElement(item);
        if (!element) {
          // Non-Element items inside the list still get walked so nested
          // Elements below them are visited. `everywhereStoppable` returns
          // the same reference when nothing changed, so a reference diff
          // is a sufficient change signal.
          const walkedItem = Expressions.everywhereStoppable(walker)(item);
          if (walkedItem !== item) listChanged = true;
          nextItems.push(walkedItem);
          continue;
        }

        const result = patchElement(element);
        if (!result.changed) {
          // Preserve unchanged items by reference so untouched slots don't
          // drift.
          nextItems.push(item);
          continue;
        }

        // Splice every reified root into the new list: 0 shrinks, 1 keeps
        // the length, >1 grows.
        nextItems.push(...result.expressions);
        listChanged = true;
      }

      if (listChanged) {
        return Expressions.stop(EList(nextItems));
      }
      return Expressions.stop(expr);
    }

    // Bare Element position: single-root contract.
    const element = Expressions.getElement(expr);
    if (element) {
      const result = patchElement(element);
      if (!result.changed) return Expressions.stop(expr);

      if (result.expressions.length !== 1) {
        reportWarning(
          'MalformedDefaultDataPatch',
          `Skipped defaultData element "${element.id}" on node "${jsonNodeId}"; ` +
            `patch produced ${result.expressions.length} root expression(s), expected exactly 1.`
        );
        return Expressions.stop(expr);
      }

      return Expressions.stop(result.expressions[0]!);
    }

    // Everything else: keep walking.
    return Expressions.resume(expr);
  };

  return Expressions.everywhereStoppable(walker)(defaultData);
};

/**
 * Only symbol roots persist `defaultData` today; symbol instances read the
 * effective default off the root at reify time. Keeping the guard here means
 * `applyPatch` can call this unconditionally.
 */
const isDefaultDataOwner = (jsonNode: JSONNode): boolean =>
  DomTypeUtils.isSymbolRoot(jsonNode.data);

/**
 * Derive the component map that the Element → JSONNode export half of the
 * defaultData round trip needs (`exportElementAsJSONNodeArray`).
 *
 * Plugin components come straight from `plugins`. User components are derived
 * from the `dataTypes` passed alongside them — `extraContext.dataTypes` holds
 * one entry per user component under the `__SitePlugin` namespace (see
 * `dataTypesFromSymbolNodes`), so the export half recognizes user-component
 * instances inside `defaultData` without rebuilding a full component map from
 * `symbolJsonNodes`. Without the user-component half of the map, an instance
 * inside `defaultData` takes the corrupt-instance path in
 * `shouldStopWalking` (`packages/systems/tree/ExportElementUtils.ts`):
 * `sym.overrides` are passed through raw instead of flattened to node ids,
 * and an instance with no literal children stops the export walk before
 * nested content is reached. With the map, the instance exports as a
 * `Symbol` node with `children: []` and flattened `sym.overrides`, so the
 * patch harness (which descends via `children`) never walks the override
 * subtree.
 *
 * The export path only reads `dataType` from each component (plus key
 * presence, via `isComponentInst`/`isUserComponentInst`), so user components
 * are stubbed with every other field at its `Component` default; `render` is
 * required by the `Component` shape but is never read by the export path, so
 * an empty hole fills it.
 *
 * Memoized on `(plugins, dataTypes)` identity with nested weakMemo (matching
 * `dataTypesFromSymbolNodes`), so a single migration pass over many symbol
 * nodes only pays for one derivation even though `applyPatchToDefaultData` is
 * called per node.
 */
const componentsFromPluginsAndDataTypes = weakMemo(
  (plugins: Plugins.PluginsType) =>
    weakMemo((dataTypes: Plugins.DataTypes): Component.ComponentMap => {
      const sitePluginName = Plugins.name('__SitePlugin');
      const userComponentDataTypes = dataTypes.byPlugin()[sitePluginName] ?? {};

      return Object.entries(userComponentDataTypes).reduce(
        (componentMap, [componentId, dataType]) =>
          componentMap.set(
            qualify(sitePluginName, Component.name(componentId)),
            Component.reify({render: EHole(), dataType})
          ),
        Plugins.getPluginComponents(plugins)
      );
    })
);

/**
 * Applies `patchToApply` to element subtrees stored on
 * `jsonNode.data.sym.defaultData`. Mirrors `applyPatchToDefaultValues`.
 */
export const applyPatchToDefaultData = (
  jsonNode: JSONNode,
  patchToApply: NodePatch,
  extraFun: {
    reportWarning: WarningReporter;
  },
  extraContext: EnhancedExtraContext,
  reportChange?: (arg1: ReportType) => void
): void => {
  if (!extraContext.plugins) return;
  if (!isDefaultDataOwner(jsonNode)) return;

  const rawDefaultData = jsonNode.data?.sym?.defaultData;
  if (rawDefaultData === undefined) return;
  const defaultData = parseDefaultData(rawDefaultData);
  if (!defaultData) return;

  const expression = walkDefaultDataElements(
    defaultData,
    extraContext.plugins,
    extraContext.dataTypes,
    (defaultDataNodeTree) => {
      let anyNeeded = false;
      const nodes = JsonDomUtils.processNodes(
        defaultDataNodeTree,
        (context: Context) => {
          const {node} = context;
          context.extraContext = extraContext;

          const {needed, apply, logData} = patchToApply(context, extraFun);

          if (needed) {
            anyNeeded = true;
            const newSubtree = apply() || [node.jsonNode];
            node.newSubtree = newSubtree;
            // @ts-expect-error when we're removing a jsonNode, it becomes undefined.
            node.jsonNode = newSubtree.length ? newSubtree[0] : undefined;

            if (reportChange && logData.meta) {
              logData.meta.defaultData = true;
              reportChange(logData);
            }
          }

          return node.newSubtree;
        }
      );
      return {nodes, changed: anyNeeded};
    },
    extraFun.reportWarning,
    jsonNode._id
  );

  if (expression !== defaultData && jsonNode.data?.sym) {
    jsonNode.data.sym.defaultData = stringifyExpression(expression);
  }
};

/**
 * Read-only regression detection variant. Mirrors
 * `detectRegressionsDefaultValues`.
 */
export const detectRegressionsDefaultData = (
  jsonNode: JSONNode,
  patchToApply: NodePatch,
  extraFun: {
    reportWarning: WarningReporter;
  },
  extraContext: EnhancedExtraContext,
  reportChange?: (arg1: ReportType) => void
): void => {
  if (!extraContext.plugins) return;
  if (!isDefaultDataOwner(jsonNode)) return;

  const rawDefaultData = jsonNode.data?.sym?.defaultData;
  if (rawDefaultData === undefined) return;
  const defaultData = parseDefaultData(rawDefaultData);
  if (!defaultData) return;

  walkDefaultDataElements(
    defaultData,
    extraContext.plugins,
    extraContext.dataTypes,
    (defaultDataNodeTree) => {
      JsonDomUtils.processNodes(defaultDataNodeTree, (context: Context) => {
        context.extraContext = extraContext;

        const {needed, logData} = patchToApply(context, extraFun);

        if (needed && reportChange && logData.meta) {
          logData.meta.defaultData = true;
          reportChange(logData);
        }
      });
      // Detection never rewrites the tree, so nothing propagates back to the
      // walker and no reification happens.
      return {nodes: defaultDataNodeTree, changed: false};
    },
    extraFun.reportWarning,
    jsonNode._id
  );
};

export const testUtils = {
  walkDefaultDataElements,
} as const;
